import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'contact/<contactId>/automation';

interface PagedResponse<T> {
	values: T[];
	pageToken?: string;
	totalCount?: number;
}

export function useContactAutomationsQuery({
	contactId,
	enabled = true,
	impersonationContext,
}: {
	contactId: string;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!contactId,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callAsync<PagedResponse<Api.IAutomation>>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `contact/${encodeURIComponent(contactId)}/Automation`,
					impersonationContext,
				}),
				'GET'
			);
			// API returns paginated response with values array
			return response.values || [];
		},
		queryKey: [BASE_QUERY_KEY, contactId, impersonationContext?.account?.id],
	});
}

export function invalidateAllContactAutomationsQueries() {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
}

// POST Automation/{id}/Contact only queues the creation job, so a fetch issued the moment it
// returns still comes back without the new automation. Offsets from that request, spread wide
// enough to outlast a busy job worker.
const QUEUED_START_RECHECK_DELAYS_MS = [1_000, 3_000, 6_000, 12_000, 20_000];

let queuedStartRecheckTimeouts: ReturnType<typeof setTimeout>[] = [];

/**
 * Re-checks a contact's automations on a bounded schedule after a queued start request, so the
 * in-progress banner picks the automation up once the job creates it instead of staying empty
 * until the next page load. Only one schedule runs at a time; a new start replaces the old one.
 */
export function recheckContactAutomationsAfterQueuedStart() {
	cancelContactAutomationsRechecks();
	queuedStartRecheckTimeouts = QUEUED_START_RECHECK_DELAYS_MS.map(delayMs =>
		setTimeout(invalidateAllContactAutomationsQueries, delayMs)
	);
}

function cancelContactAutomationsRechecks() {
	queuedStartRecheckTimeouts.forEach(clearTimeout);
	queuedStartRecheckTimeouts = [];
}
